import { useState, useEffect } from 'react';
import { api, shareSettingsApi, memberShareApi, eventShareApi, contributionApi } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import type { Member, CarouselEvent, ContributionAsk } from '../../types';
import { parseEventDate } from '../../utils/eventDate';
import { ContributionAskFields, emptyContributionAsk, hasAnyAsk } from '../../components/ContributionAskFields';

export function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [role, setRole] = useState<'member' | 'viewer'>('member');
  const [addScope, setAddScope] = useState<'all' | 'specific'>('all');
  const [addEventIds, setAddEventIds] = useState<Set<string>>(new Set());
  const [addEvents, setAddEvents] = useState<CarouselEvent[]>([]);
  const [showAddAsk, setShowAddAsk] = useState(false);
  const [addAsk, setAddAsk] = useState<ContributionAsk>(emptyContributionAsk());
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRelationship, setEditRelationship] = useState('');
  const [petName, setPetName] = useState('');
  const [petType, setPetType] = useState('dog');
  const [petRelationship, setPetRelationship] = useState('');
  const [petLoading, setPetLoading] = useState(false);
  const [showAddPet, setShowAddPet] = useState(false);
  const [expandedScopeId, setExpandedScopeId] = useState<string | null>(null);
  const [askPanelId, setAskPanelId] = useState<string | null>(null);
  const [giveAccessId, setGiveAccessId] = useState<string | null>(null);
  const [giveAccessEmail, setGiveAccessEmail] = useState('');
  const [giveAccessRole, setGiveAccessRole] = useState<'member' | 'viewer'>('member');
  const [giveAccessLoading, setGiveAccessLoading] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get<Member[]>('/account/members').then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // Pre-fill convenience only — reads the account's legacy share-settings value
    // once to seed the add form's scope control; scope is per-member going forward,
    // not a live account-wide enforcement toggle anymore (see the plan).
    shareSettingsApi.get().then(s => setAddScope(s.shareAllWithFriends ? 'all' : 'specific')).catch(() => {});
  }, []);

  useEffect(() => {
    if (addScope === 'specific' && addEvents.length === 0) {
      api.get<{ events?: CarouselEvent[] }>('/events?sort=date').then(r => setAddEvents(r.data.events ?? [])).catch(() => {});
    }
  }, [addScope, addEvents.length]);

  async function handleAdd(e: { preventDefault(): void }) {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    try {
      const wantsAsk = role === 'member' && addScope === 'specific' && addEventIds.size > 0 && showAddAsk && hasAnyAsk(addAsk);
      const { data } = await api.post('/account/members', {
        name, email: email || undefined, relationship: relationship || undefined, role,
        scope: addScope,
        eventIds: addScope === 'specific' ? Array.from(addEventIds) : undefined,
        contributionRequest: wantsAsk ? addAsk : undefined,
      });
      setMembers(prev => [...prev, data]);
      setName(''); setEmail(''); setRelationship(''); setRole('member');
      setAddScope('all'); setAddEventIds(new Set()); setShowAddAsk(false); setAddAsk(emptyContributionAsk());
      if (email && data.emailDelivered === false) {
        setError(`${data.user.name} was added, but the invite email failed to send — try resending it below.`);
      } else if (data.accessEmailHeld) {
        setSuccess(`${data.user.name} added. They'll get an email when you add your first memory.`);
        setTimeout(() => setSuccess(''), 6000);
      } else {
        setSuccess(email ? `${data.user.name} added — invitation sent.` : `${data.user.name} added.`);
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this person from your circle?')) return;
    await api.delete(`/account/members/${id}`);
    setMembers(prev => prev.filter(m => m.id !== id));
    if (expandedScopeId === id) setExpandedScopeId(null);
    if (askPanelId === id) setAskPanelId(null);
  }

  async function handleChangeRole(id: string, newRole: 'member' | 'viewer') {
    await memberShareApi.changeRole(id, newRole);
    setMembers(prev => prev.map(m => m.id === id ? { ...m, role: newRole } : m));
  }

  function startEdit(member: Member) {
    setEditId(member.id);
    setEditName(member.user.name);
    setEditEmail('');
    setEditRelationship(member.relationship || '');
    setGiveAccessId(null);
    setError('');
  }

  function renderEditForm(member: Member) {
    const hasEmail = Boolean(member.user.email);
    return (
      <div className="border-t border-indigo-100 px-4 py-3 bg-indigo-50">
        <p className="text-xs font-semibold text-slate-600 mb-2">Edit {member.user.name}</p>
        <div className="space-y-2">
          <div>
            <label htmlFor={`edit-name-${member.id}`} className="block text-xs font-medium text-stone-500 mb-1">Name</label>
            <input id={`edit-name-${member.id}`} type="text" value={editName} onChange={e => setEditName(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
          </div>
          <div>
            <label htmlFor={`edit-relationship-${member.id}`} className="block text-xs font-medium text-stone-500 mb-1">Relationship</label>
            <input id={`edit-relationship-${member.id}`} type="text" value={editRelationship} onChange={e => setEditRelationship(e.target.value)}
              placeholder="e.g. Mother, Brother, Friend"
              autoCorrect="off" autoCapitalize="off" spellCheck={false}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
          </div>
          {!hasEmail && (
            <div>
              <label htmlFor={`edit-email-${member.id}`} className="block text-xs font-medium text-stone-500 mb-1">Email (sends invitation)</label>
              <input id={`edit-email-${member.id}`} type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
            </div>
          )}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => handleSaveEdit(member.id, hasEmail)} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
              {!hasEmail && editEmail ? 'Save & Invite' : 'Save'}
            </button>
            <button onClick={() => setEditId(null)} className="px-3 py-2 bg-stone-200 text-stone-700 rounded-lg text-sm hover:bg-stone-300">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  async function handleSaveEdit(id: string, hasEmail: boolean) {
    setError('');
    const payload: { name?: string; email?: string; relationship?: string } = {};
    if (editName.trim()) payload.name = editName.trim();
    if (!hasEmail && editEmail.trim()) payload.email = editEmail.trim();
    payload.relationship = editRelationship.trim() || '';
    try {
      const { data } = await api.put(`/account/members/${id}`, payload);
      setMembers(prev => prev.map(m => m.id === id ? data : m));
      setEditId(null);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed');
    }
  }

  async function handleSetScope(id: string, scope: 'all' | 'specific') {
    setMembers(prev => prev.map(m => m.id === id ? { ...m, scope } : m));
    await memberShareApi.setScope(id, scope);
    if (scope === 'all') setExpandedScopeId(null);
  }

  async function handleAddPet(e: { preventDefault(): void }) {
    e.preventDefault();
    if (!petName.trim()) return;
    setPetLoading(true);
    setError(''); setSuccess('');
    try {
      const { data } = await api.post('/account/members', {
        name: petName.trim(),
        relationship: petRelationship.trim() || undefined,
        petType,
      });
      setMembers(prev => [...prev, data]);
      setPetName(''); setPetType('dog'); setPetRelationship(''); setShowAddPet(false);
      setSuccess(`${data.user.name} added to your circle.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to add pet');
    } finally {
      setPetLoading(false);
    }
  }

  async function handleResendInvite(id: string, name: string) {
    setResendingId(id);
    setError(''); setSuccess('');
    try {
      const { data } = await api.post(`/account/members/${id}/resend-invite`);
      if (data.emailDelivered === false) {
        setError(`Invite link refreshed for ${name}, but the email failed to send. Try again later.`);
      } else {
        setSuccess(`Invitation resent to ${name}.`);
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  }

  async function handleGiveAccess(id: string) {
    if (!giveAccessEmail.trim()) return;
    setGiveAccessLoading(true);
    setError('');
    try {
      const { data } = await api.put(`/account/members/${id}`, {
        email: giveAccessEmail.trim(),
        role: giveAccessRole,
      });
      setMembers(prev => prev.map(m => m.id === id ? data : m));
      setGiveAccessId(null);
      const sentEmail = giveAccessEmail.trim();
      setGiveAccessEmail('');
      if (data.emailDelivered === false) {
        setError(`Access granted, but the invite email to ${sentEmail} failed to send — try resending it below.`);
      } else {
        setSuccess(`Invitation sent to ${sentEmail}.`);
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to send invitation');
    } finally {
      setGiveAccessLoading(false);
    }
  }

  const statusLabel: Record<string, string> = { pending: 'Invited', active: 'Active', no_email: 'No email' };
  const tagOnlyMembers = members.filter(m => !m.petType && m.status === 'no_email');
  const fullMembers = members.filter(m => m.role !== 'viewer' && !m.petType && m.status !== 'no_email');
  const viewers = members.filter(m => m.role === 'viewer' && !m.petType && m.status !== 'no_email');
  const pets = members.filter(m => m.petType);

  function toggleAddEvent(eventId: string) {
    setAddEventIds(prev => {
      const n = new Set(prev);
      if (n.has(eventId)) n.delete(eventId); else n.add(eventId);
      return n;
    });
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-serif font-bold text-stone-800 mb-1">My Circle</h1>
      <p className="text-sm text-slate-500 mb-6">
        Add the people in your life. Give them <strong>Full Access</strong> to see (and add to) your
        archive, or <strong>View Only</strong> to just look — and choose whether that covers everything
        or just specific memories.
      </p>

      {/* Add member form */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 mb-6">
        <h2 className="text-sm font-semibold text-stone-700 mb-3">Add a person</h2>
        <form onSubmit={handleAdd} className="space-y-2">
          <div className="flex gap-3 flex-wrap">
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              required placeholder="Name *"
              className="flex-1 min-w-32 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email (optional)"
              className="flex-1 min-w-40 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <input
              type="text" value={relationship} onChange={e => setRelationship(e.target.value)}
              placeholder="Relationship (e.g. Mother, Brother, Friend)"
              autoCorrect="off" autoCapitalize="off" spellCheck={false}
              className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex rounded-lg border border-stone-300 overflow-hidden text-sm flex-shrink-0">
              <button type="button" onClick={() => setRole('member')}
                className={`px-3 py-2 font-medium transition-colors ${role === 'member' ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                Full access
              </button>
              <button type="button" onClick={() => setRole('viewer')}
                className={`px-3 py-2 font-medium transition-colors border-l border-stone-300 ${role === 'viewer' ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                View only
              </button>
            </div>
          </div>

          {/* Scope — per-person, works the same for either access level */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs font-medium text-slate-500">Covers:</span>
            <div className="flex rounded-lg border border-stone-300 overflow-hidden text-xs">
              <button type="button" onClick={() => setAddScope('all')}
                className={`px-2.5 py-1.5 font-medium transition-colors ${addScope === 'all' ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                All events
              </button>
              <button type="button" onClick={() => setAddScope('specific')}
                className={`px-2.5 py-1.5 font-medium transition-colors border-l border-stone-300 ${addScope === 'specific' ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                Specific events
              </button>
            </div>
          </div>

          {addScope === 'specific' && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
              <p className="text-xs text-slate-500 mb-1.5">Pick which events:</p>
              {addEvents.length === 0 ? (
                <p className="text-xs text-slate-400">No events yet.</p>
              ) : (
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {addEvents.map(evt => {
                    const checked = addEventIds.has(evt.id);
                    return (
                      <li key={evt.id}>
                        <button type="button" onClick={() => toggleAddEvent(evt.id)}
                          className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-left transition-colors ${checked ? 'bg-indigo-50' : 'hover:bg-slate-100'}`}>
                          <div className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 ${checked ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`} />
                          <span className="text-xs text-slate-700 truncate">{evt.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {role === 'member' && addScope === 'specific' && addEventIds.size > 0 && (
            <div>
              {!showAddAsk ? (
                <button type="button" onClick={() => setShowAddAsk(true)} className="text-xs font-medium text-indigo-600 hover:underline">
                  + Ask them to help complete {addEventIds.size === 1 ? 'this event' : 'these events'}
                </button>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">Ask them to help</span>
                    <button type="button" onClick={() => setShowAddAsk(false)} className="text-xs text-slate-400 hover:text-slate-600">Remove</button>
                  </div>
                  <ContributionAskFields value={addAsk} onChange={setAddAsk} />
                </div>
              )}
            </div>
          )}

          <div>
            <button type="submit" disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {loading ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        {success && <p className="text-green-700 text-sm mt-2 font-medium">{success}</p>}
      </div>

      {/* Add pet */}
      {!showAddPet ? (
        <button
          onClick={() => setShowAddPet(true)}
          className="w-full flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors"
        >
          <span className="text-base">🐾</span> Add a pet to your circle
        </button>
      ) : (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-6">
          <h2 className="text-sm font-semibold text-stone-700 mb-3">Add a pet</h2>
          <form onSubmit={handleAddPet} className="space-y-2">
            <div className="flex gap-3 flex-wrap">
              <input
                type="text" value={petName} onChange={e => setPetName(e.target.value)}
                required placeholder="Pet's name *"
                className="flex-1 min-w-32 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              />
              <select
                value={petType} onChange={e => setPetType(e.target.value)}
                className="border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              >
                <option value="dog">Dog</option>
                <option value="cat">Cat</option>
                <option value="bird">Bird</option>
                <option value="rabbit">Rabbit</option>
                <option value="horse">Horse</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex gap-3">
              <input
                type="text" value={petRelationship} onChange={e => setPetRelationship(e.target.value)}
                placeholder="Nickname or note (optional)"
                className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              />
              <button type="submit" disabled={petLoading || !petName.trim()}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                Add Pet
              </button>
              <button type="button" onClick={() => setShowAddPet(false)}
                className="px-4 py-2 bg-white text-stone-600 border border-stone-200 rounded-lg text-sm hover:bg-stone-50">
                Cancel
              </button>
            </div>
          </form>
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        </div>
      )}

      {/* Pets */}
      {pets.length > 0 && (
        <>
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Pets</h2>
          <div className="space-y-2 mb-6">
            {pets.map(member => (
              <div key={member.id} className="bg-white rounded-xl px-4 py-3 shadow-sm border border-amber-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center text-lg flex-shrink-0">🐾</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{member.user.name}</p>
                  <p className="text-xs text-stone-400 capitalize truncate">
                    {member.petType}
                    {member.relationship && <> · <span className="text-amber-600 font-medium">{member.relationship}</span></>}
                  </p>
                </div>
                <button onClick={() => handleDelete(member.id)} className="text-xs text-red-500 hover:underline flex-shrink-0">Remove</button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Full-access members */}
      <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Full Access</h2>
      <div className="space-y-2 mb-6">
        {fullMembers.length === 0 && <p className="text-stone-400 text-sm">No full-access people yet.</p>}
        {fullMembers.map(member => {
          const isOwner = member.userId === user?.id;
          return (
            <div key={member.id} className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${isOwner ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'}`}>
                  {(member.user.name?.[0] ?? '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">
                    {member.user.name}
                    {isOwner && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-full">You</span>}
                  </p>
                  <p className="text-xs text-stone-400 truncate">
                    {member.user.email || <span className="italic">No email</span>}
                    {!isOwner && <> · <span className={member.status === 'active' ? 'text-green-600' : 'text-stone-400'}>{statusLabel[member.status]}</span></>}
                    {member.relationship && <> · <span className="text-indigo-500 font-medium">{member.relationship}</span></>}
                    {' · '}<span className="text-slate-400">{member.scope === 'all' ? 'All events' : 'Specific events'}</span>
                  </p>
                </div>
                {!isOwner && (
                  <div className="flex gap-2 flex-shrink-0 items-center flex-wrap justify-end">
                    {member.status === 'pending' && (
                      <button
                        onClick={() => handleResendInvite(member.id, member.user.name)}
                        disabled={resendingId === member.id}
                        className="text-xs text-indigo-500 hover:underline disabled:opacity-50 whitespace-nowrap"
                      >
                        {resendingId === member.id ? 'Sending…' : 'Resend invite'}
                      </button>
                    )}
                    <button
                      onClick={() => setExpandedScopeId(v => v === member.id ? null : member.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap ${expandedScopeId === member.id ? 'bg-indigo-600 text-white border-indigo-600' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}
                    >
                      {expandedScopeId === member.id ? 'Done' : 'Events'}
                    </button>
                    <button
                      onClick={() => setAskPanelId(v => v === member.id ? null : member.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap ${askPanelId === member.id ? 'bg-indigo-600 text-white border-indigo-600' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}
                    >
                      Ask to contribute
                    </button>
                    {editId !== member.id && (
                      <button onClick={() => startEdit(member)} className="text-xs text-indigo-600 hover:underline">Edit</button>
                    )}
                    <button onClick={() => handleChangeRole(member.id, 'viewer')} className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap">→ View only</button>
                    <button onClick={() => handleDelete(member.id)} className="text-xs text-red-600 hover:underline">Remove</button>
                  </div>
                )}
              </div>
              {editId === member.id && renderEditForm(member)}
              {expandedScopeId === member.id && (
                <EventSharePanel memberId={member.id} memberName={member.user.name} scope={member.scope} onScopeChange={s => handleSetScope(member.id, s)} />
              )}
              {askPanelId === member.id && (
                <AskForHelpPanel
                  memberId={member.id}
                  memberName={member.user.name}
                  memberScope={member.scope}
                  onSent={msg => { setAskPanelId(null); setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }}
                  onCancel={() => setAskPanelId(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* View-only members */}
      <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">View Only</h2>
      <div className="space-y-2 mb-6">
        {viewers.length === 0 && (
          <p className="text-stone-400 text-sm">
            No view-only people yet. Add one above with "View only" access to share specific events with them.
          </p>
        )}
        {viewers.map(member => (
          <div key={member.id} className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
            {/* Row */}
            <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
              <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {(member.user.name?.[0] ?? '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{member.user.name}</p>
                <p className="text-xs text-stone-400 truncate">
                  {member.user.email || <span className="italic">No email</span>}
                  {' · '}<span className={member.status === 'active' ? 'text-green-600' : 'text-stone-400'}>{statusLabel[member.status]}</span>
                  {member.relationship && <> · <span className="text-indigo-500 font-medium">{member.relationship}</span></>}
                  {' · '}<span className="text-slate-400">{member.scope === 'all' ? 'All events' : 'Specific events'}</span>
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0 items-center flex-wrap justify-end">
                {member.status === 'pending' && (
                  <button
                    onClick={() => handleResendInvite(member.id, member.user.name)}
                    disabled={resendingId === member.id}
                    className="text-xs text-indigo-500 hover:underline disabled:opacity-50 whitespace-nowrap"
                  >
                    {resendingId === member.id ? 'Sending…' : 'Resend invite'}
                  </button>
                )}
                <button
                  onClick={() => setExpandedScopeId(v => v === member.id ? null : member.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap ${expandedScopeId === member.id ? 'bg-indigo-600 text-white border-indigo-600' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}
                >
                  {expandedScopeId === member.id ? 'Done' : 'Events'}
                </button>
                <button onClick={() => handleChangeRole(member.id, 'member')} className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap">→ Full</button>
                <button onClick={() => handleDelete(member.id)} className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
            </div>

            {/* Expandable event sharing panel — view-only people never see the "Ask to contribute" action, matching the confirmed design */}
            {expandedScopeId === member.id && (
              <EventSharePanel memberId={member.id} memberName={member.user.name} scope={member.scope} onScopeChange={s => handleSetScope(member.id, s)} />
            )}
          </div>
        ))}
      </div>

      {/* Tagged-only members (no app access) */}
      {tagOnlyMembers.length > 0 && (
        <>
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Tagged only — no app access</h2>
          <p className="text-xs text-slate-400 mb-2">
            These people are in your photos but don't have an account. Give them access to send an invitation.
          </p>
          <div className="space-y-2 mb-6">
            {tagOnlyMembers.map(member => (
              <div key={member.id} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
                  <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {(member.user.name?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{member.user.name}</p>
                    <p className="text-xs text-slate-400 truncate">
                      No access
                      {member.relationship && <> · <span className="text-slate-500 font-medium">{member.relationship}</span></>}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 items-center flex-wrap justify-end">
                    <button
                      onClick={() => {
                        setGiveAccessId(v => v === member.id ? null : member.id);
                        setGiveAccessEmail('');
                        setGiveAccessRole('member');
                        setEditId(null);
                        setError('');
                      }}
                      className="text-xs px-2.5 py-1 rounded-full border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors"
                    >
                      Give access
                    </button>
                    {editId !== member.id && (
                      <button onClick={() => startEdit(member)} className="text-xs text-slate-400 hover:text-slate-600">Edit</button>
                    )}
                    <button onClick={() => handleDelete(member.id)} className="text-xs text-red-500 hover:underline">Remove</button>
                  </div>
                </div>

                {editId === member.id && renderEditForm(member)}

                {giveAccessId === member.id && (
                  <div className="border-t border-slate-100 px-4 py-3 bg-indigo-50">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Send {member.user.name} an invitation</p>
                    <div className="flex gap-2 flex-wrap mb-2">
                      <input
                        type="email"
                        value={giveAccessEmail}
                        onChange={e => setGiveAccessEmail(e.target.value)}
                        placeholder="Their email address *"
                        className="flex-1 min-w-40 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      />
                      <div className="flex rounded-lg border border-slate-300 overflow-hidden text-sm flex-shrink-0 bg-white">
                        <button type="button" onClick={() => setGiveAccessRole('member')}
                          className={`px-3 py-2 font-medium transition-colors ${giveAccessRole === 'member' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                          Full access
                        </button>
                        <button type="button" onClick={() => setGiveAccessRole('viewer')}
                          className={`px-3 py-2 font-medium transition-colors border-l border-slate-300 ${giveAccessRole === 'viewer' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                          View only
                        </button>
                      </div>
                    </div>
                    {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleGiveAccess(member.id)}
                        disabled={!giveAccessEmail.trim() || giveAccessLoading}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {giveAccessLoading ? 'Sending…' : 'Send invitation'}
                      </button>
                      <button
                        onClick={() => setGiveAccessId(null)}
                        className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );
}

// ─── Per-member event scope panel — works for either access level now ────────

function EventSharePanel({ memberId, memberName, scope, onScopeChange }: {
  memberId: string; memberName: string; scope: 'all' | 'specific'; onScopeChange: (scope: 'all' | 'specific') => void;
}) {
  const [events, setEvents] = useState<CarouselEvent[]>([]);
  const [sharedEventIds, setSharedEventIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ events?: CarouselEvent[]; mode?: string }>('/events?sort=date').then(r => r.data.events ?? []),
      memberShareApi.getEventIds(memberId),
    ]).then(([evts, ids]) => {
      setEvents(evts);
      setSharedEventIds(new Set(ids));
    }).finally(() => setLoading(false));
  }, [memberId]);

  async function toggle(eventId: string) {
    const shared = sharedEventIds.has(eventId);
    setToggling(eventId);
    try {
      if (shared) {
        await eventShareApi.unshare(eventId, memberId);
        setSharedEventIds(prev => { const n = new Set(prev); n.delete(eventId); return n; });
      } else {
        await eventShareApi.share(eventId, [memberId]);
        setSharedEventIds(prev => new Set([...prev, eventId]));
      }
    } finally {
      setToggling(null);
    }
  }

  if (loading) return <div className="px-4 pb-3 text-xs text-slate-400">Loading events…</div>;

  return (
    <div className="border-t border-stone-100 px-4 py-3 bg-slate-50">
      {scope === 'all' ? (
        <p className="text-xs text-slate-500 italic">
          All events are included automatically for {memberName}.{' '}
          <button type="button" onClick={() => onScopeChange('specific')} className="not-italic font-medium text-indigo-600 hover:underline">
            Switch to specific events
          </button>
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-2 flex items-center justify-between">
            <span>Pick which events {memberName} can see:</span>
            <button type="button" onClick={() => onScopeChange('all')} className="font-medium text-indigo-600 hover:underline flex-shrink-0 ml-2">
              Include all events instead
            </button>
          </p>
          {events.length === 0 ? (
            <p className="text-xs text-slate-400">No events yet.</p>
          ) : (
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {events.map(evt => {
                const shared = sharedEventIds.has(evt.id);
                const busy = toggling === evt.id;
                return (
                  <li key={evt.id}>
                    <button
                      onClick={() => toggle(evt.id)}
                      disabled={busy}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left transition-colors ${shared ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-slate-100'}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${shared ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                        {shared && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                          </svg>
                        )}
                        {busy && <div className="w-2 h-2 rounded-full border border-indigo-300 border-t-transparent animate-spin" />}
                      </div>
                      <span className="text-xs text-slate-700 truncate">{evt.title}</span>
                      {evt.eventDate && (
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-auto">
                          {parseEventDate(evt.eventDate).getFullYear()}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ─── "Ask to contribute" panel — attach a contribution request to one or more events ─
// This is template #3's entry point: a full-access member (scope='all' or
// 'specific') gets asked about one or more particular events without changing
// their overall access.

function AskForHelpPanel({ memberId, memberName, memberScope, onSent, onCancel }: {
  memberId: string; memberName: string; memberScope: 'all' | 'specific';
  onSent: (message: string) => void; onCancel: () => void;
}) {
  const [events, setEvents] = useState<CarouselEvent[]>([]);
  const [eventIds, setEventIds] = useState<Set<string>>(new Set());
  const [ask, setAsk] = useState<ContributionAsk>(emptyContributionAsk());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // scope='specific' members can only be asked about events already in their
    // list (asking beyond it would fail the backend's live write-access check);
    // scope='all' members can be asked about anything.
    (memberScope === 'specific'
      ? memberShareApi.getEventIds(memberId).then(async ids => {
          const all = await api.get<{ events?: CarouselEvent[] }>('/events?sort=date').then(r => r.data.events ?? []);
          return all.filter(e => ids.includes(e.id));
        })
      : api.get<{ events?: CarouselEvent[] }>('/events?sort=date').then(r => r.data.events ?? [])
    ).then(setEvents).finally(() => setLoading(false));
  }, [memberId, memberScope]);

  function toggleEvent(eventId: string) {
    setEventIds(prev => { const n = new Set(prev); if (n.has(eventId)) n.delete(eventId); else n.add(eventId); return n; });
  }

  async function handleSend() {
    if (eventIds.size === 0 || !hasAnyAsk(ask)) return;
    setSending(true);
    setError('');
    try {
      await contributionApi.create({ eventIds: Array.from(eventIds), memberIds: [memberId], ...ask });
      onSent(`Asked ${memberName} for help on ${eventIds.size === 1 ? 'that event' : `${eventIds.size} events`}.`);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to send request');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="border-t border-indigo-100 px-4 py-3 bg-indigo-50/40 space-y-2.5">
      <p className="text-xs font-semibold text-slate-600">Ask {memberName} to help complete the story of…</p>
      {loading ? (
        <p className="text-xs text-slate-400">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="text-xs text-slate-400">
          {memberScope === 'specific' ? "No events shared with them yet — share one first." : 'No events yet.'}
        </p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto bg-white rounded-lg border border-slate-200 p-1.5">
          {events.map(evt => {
            const checked = eventIds.has(evt.id);
            return (
              <li key={evt.id}>
                <button type="button" onClick={() => toggleEvent(evt.id)}
                  className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-left transition-colors ${checked ? 'bg-indigo-50' : 'hover:bg-slate-100'}`}>
                  <div className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 ${checked ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`} />
                  <span className="text-xs text-slate-700 truncate">{evt.title}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {eventIds.size > 0 && <ContributionAskFields value={ask} onChange={setAsk} />}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={handleSend}
          disabled={sending || eventIds.size === 0 || !hasAnyAsk(ask)}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {sending ? 'Sending…' : 'Send request'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-xs hover:bg-slate-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}
